import { idiomaTarefas, traduzirErroTarefas, traduzirTarefas as tt } from './tarefasI18n';
// Utilitarios do modulo de Tarefas, com textos no namespace tasks do i18next.
import api from '../../services/api';

export const STATUS = [
  { valor: 'pendente', get rotulo() { return tt('pending'); } },
  { valor: 'a_fazer', get rotulo() { return tt('toDo'); } },
  { valor: 'em_andamento', get rotulo() { return tt('inProgress'); } },
  { valor: 'em_revisao', get rotulo() { return tt('inReview'); } },
  { valor: 'concluida', get rotulo() { return tt('completed'); } }
];

export const PRIORIDADES = [
  { valor: 'urgente', get rotulo() { return tt('urgent'); } },
  { valor: 'alta', get rotulo() { return tt('high'); } },
  { valor: 'media', get rotulo() { return tt('medium'); } },
  { valor: 'baixa', get rotulo() { return tt('low'); } }
];

export const diasSemana = () => [tt('sunday'), tt('monday'), tt('tuesday'), tt('wednesday'), tt('thursday'), tt('friday'), tt('saturday')];

export const rotuloStatus = (valor) => STATUS.find((s) => s.valor === valor)?.rotulo || valor;
export const rotuloPrioridade = (valor) => PRIORIDADES.find((p) => p.valor === valor)?.rotulo || valor;

// 3725 -> "1h02min"; 45 -> "0min"
export const formatarDuracao = (segundos) => {
  const total = Math.max(0, Math.floor(Number(segundos) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h === 0) return tt('durationMinutes', { minutes: m });
  return tt('durationHoursMinutes', { hours: h, minutes: String(m).padStart(2, '0') });
};

// Versao curta para o cartao do quadro: 2700 -> "45min", 6300 -> "1h45".
export const formatarDuracaoCurta = (segundos) => {
  const total = Math.max(0, Math.floor(Number(segundos) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h === 0) return tt('durationMinutes', { minutes: m });
  return tt('durationShort', { hours: h, minutes: String(m).padStart(2, '0') });
};

// Cronometro: 3725 -> "01:02:05"
export const formatarCronometro = (segundos) => {
  const total = Math.max(0, Math.floor(Number(segundos) || 0));
  const h = String(Math.floor(total / 3600)).padStart(2, '0');
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

// Datas de calendario usam UTC apenas para formatacao, preservando o dia da API.
export const formatarData = (valor) => {
  if (!valor) return '—';
  const data = new Date(`${String(valor).slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(data.getTime())) return String(valor);
  return data.toLocaleDateString(idiomaTarefas(), { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC' });
};

// Data curta, com o ano so quando nao e o ano corrente.
export const formatarDataCurta = (valor) => {
  if (!valor) return '';
  const data = new Date(`${String(valor).slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(data.getTime())) return String(valor);
  return data.toLocaleDateString(idiomaTarefas(), {
    day: '2-digit', month: '2-digit', timeZone: 'UTC',
    year: data.getUTCFullYear() === new Date().getFullYear() ? undefined : '2-digit'
  });
};

export const formatarDataHora = (valor) => {
  if (!valor) return '—';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '—';
  return data.toLocaleString(idiomaTarefas(), { dateStyle: 'short', timeStyle: 'short' });
};

// Estimativa em minutos <-> texto "HH:MM"
export const minutosParaTexto = (minutos) => {
  if (minutos === null || minutos === undefined || minutos === '') return '';
  const total = Number(minutos);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

export const textoParaMinutos = (texto) => {
  const valor = String(texto || '').trim();
  if (!valor) return null;
  const m = /^(\d{1,4})(?::([0-5]?\d))?$/.exec(valor);
  if (!m) return NaN;
  return Number(m[1]) * 60 + Number(m[2] || 0);
};

export const atrasada = (tarefa) => {
  if (!tarefa?.data_fim || tarefa.status === 'concluida') return false;
  const hoje = new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10);
  return String(tarefa.data_fim).slice(0, 10) < hoje;
};

export const mensagemDeErro = (err, padrao = tt('couldNotCompleteTheAction')) => {
  const dados = err?.response?.data;
  // Play recusado nas ferias: a data vem a parte, para sair no idioma do usuario.
  if (dados?.codigo === 'em_ferias') return tt('timerBlockedOnVacation', { date: formatarData(dados.ate) });
  return traduzirErroTarefas(dados?.error, padrao);
};

// O play muda em varias telas (lista, painel, cronometro da barra superior).
// Um evento no window mantem todas em dia sem acoplar os componentes.
// "agora", "há 5 min", "há 3 h", "há 10 dias".
export const tempoRelativo = (valor) => {
  const ms = Date.now() - new Date(valor).getTime();
  if (!valor || Number.isNaN(ms)) return '';
  const min = Math.floor(ms / 60000);
  if (min < 1) return tt('relativeNow');
  const relativo = new Intl.RelativeTimeFormat(idiomaTarefas(), { numeric: 'always', style: 'short' });
  if (min < 60) return relativo.format(-min, 'minute');
  const horas = Math.floor(min / 60);
  if (horas < 24) return relativo.format(-horas, 'hour');
  const dias = Math.floor(horas / 24);
  return relativo.format(-dias, 'day');
};

// Lider da equipe ou admin: distribui o trabalho e conclui/reabre tarefas.
export const lideraEquipe = (contexto, equipeId) => Boolean(
  contexto?.ehAdmin || contexto?.lidera?.map(Number).includes(Number(equipeId))
);
export const podeConcluir = (contexto, tarefa) => lideraEquipe(contexto, tarefa?.equipe_id);

// Relatorios (e o cronograma completo): so lideres e o admin.
export const podeVerRelatorios = (contexto) => Boolean(contexto?.ehAdmin || contexto?.lidera?.length);

export const EVENTO_PLAY = 'tarefas:play-alterado';
export const avisarPlayAlterado = () => window.dispatchEvent(new CustomEvent(EVENTO_PLAY));

export const darPlay = async (tarefaId) => {
  const { data } = await api.post(`/tarefas/tarefas/${tarefaId}/play`);
  avisarPlayAlterado();
  return data;
};

export const pausarPlay = async () => {
  const { data } = await api.post('/tarefas/play/pausar');
  avisarPlayAlterado();
  return data;
};

// Admin sempre; exxer com a flag `tarefas` (vem do /me).
export const temAcessoTarefas = (usuario) => (
  usuario?.tipo_usuario === 'admin' ||
  (usuario?.tipo_usuario === 'exxer' && Number(usuario?.tarefas) === 1)
);
